/**
 * useGetActiveReferralProgram — public read of the current admin-authored
 * referral program. Drives the dynamic copy in /account's Refer-a-Friend
 * section + the Invite a Friend modal's callout / qualifier fine print.
 *
 * Backed by GET /referrals/active-program (no auth required). Stale time
 * is generous (15 min) since the program changes infrequently and the
 * SPA only surfaces it on a couple of marketing surfaces.
 */

import { useQuery } from "@tanstack/react-query";
import { ActiveReferralProgramResponse, ReferralsApi } from "@devour/client";
import getConfig from "@/utils/getConfig";

export const USE_GET_ACTIVE_REFERRAL_PROGRAM_KEY = "active-referral-program";

/*
 * Resilient against a stale `@devour/client/dist` that hasn't picked up
 * `getReferralsActiveProgram` yet. The deployed mirrors always have the
 * generated method (CI rebuilds the client on every tag push), but a
 * local dev clone with an out-of-date `client/dist` would otherwise
 * crash every surface that mounts the InviteModal or /account.
 *
 * `active: false` is the explicit "no program" payload the SPA already
 * handles — qualifier line suppresses, callout falls back to defaults.
 * No user-visible regression while a fresh client build is pending.
 */
async function fetchActiveReferralProgram(): Promise<ActiveReferralProgramResponse> {
    const api = new ReferralsApi(getConfig());
    const fn = (api as unknown as { getReferralsActiveProgram?: () => Promise<ActiveReferralProgramResponse> })
        .getReferralsActiveProgram;
    if (typeof fn !== "function") {
        return { active: false };
    }
    return await fn.call(api);
}

export function useGetActiveReferralProgram() {
    return useQuery({
        queryKey: [USE_GET_ACTIVE_REFERRAL_PROGRAM_KEY],
        queryFn: fetchActiveReferralProgram,
        staleTime: 15 * 60 * 1000,
    });
}
